import React, { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { loadPostCollectionAsync } from '../../data-layer/posts/actions';
import { PostModel } from '../../data-layer/posts/types';
import { toPostUI } from '../../mappers/posts/toPostUI';
import { Box } from '../../ui/layout/Box';
import { InfiniteGrid } from '../../ui/layout/InfiniteGrid';
import { PostCardCompact, PostCardCompactSkeleton } from '../../ui/posts/PostCardCompact';
import { PostContextMenu } from '../../ui/posts/PostContextMenu';
import { analyticsService, EventSource } from '../../utils/analyticsService';
import { ymTrackPostKebabClick, ymTrackProfilePostClick } from '../../utils/metrika';
import { usePostCollection } from '../post/usePostCollection';
import { withPostCallbacks } from '../post/withPostCallbacks';
import { useCurrentUser } from '../users/useCurrentUser';

const PER_PAGE = 24;

const EnhancedPostContextMenu = withPostCallbacks(PostContextMenu, EventSource.BUSINESS);

type Filters = Omit<Parameters<typeof loadPostCollectionAsync>[1], 'next'>;

export const makeBusinessPostsTab = (namespace: string, filters: Partial<Filters>) => () => {
    const params = useParams();
    const businessId = params.businessId!;
    const postCollection = usePostCollection({
        namespace: `${namespace}-${businessId}`,
        filters: {
            ...filters,
            businessId,
            amount: PER_PAGE,
        },
    });
    const currentUser = useCurrentUser();
    const onPostCardClick = useCallback(
        (id: PostModel['id'], postNumber: number) => {
            ymTrackProfilePostClick({
                page: 'Профиль бизнеса',
                userLogin: currentUser?.login,
                businessId: currentUser?.profile?.publicId,
            });
            analyticsService.trackGridTap({
                eventSource: EventSource.BUSINESS,
                postId: id,
                postNumber,
                businessId: businessId,
            });
        },
        [currentUser?.login, currentUser?.profile?.publicId, businessId],
    );

    const onPostKebabClick = useCallback(() => {
        ymTrackPostKebabClick({
            page: 'Профиль бизнеса',
            userLogin: currentUser?.login,
            businessId: currentUser?.profile?.publicId,
        });
    }, [currentUser?.login, currentUser?.profile?.publicId]);

    return (
        <InfiniteGrid
            columns={3}
            perPage={PER_PAGE}
            count={postCollection.items.length}
            isLoading={postCollection.isLoading}
            isEndReached={postCollection.isEndReached}
            onLoadNext={postCollection.onLoadNext}
            renderItem={(index: number) => {
                const postModel = postCollection.items[index];

                if (postModel) {
                    return (
                        <Box>
                            <PostCardCompact
                                fromBusinessId={businessId}
                                post={toPostUI(postModel)}
                                index={index}
                                onPostCardClick={onPostCardClick}
                                renderMenu={(trigger) => (
                                    <EnhancedPostContextMenu
                                        index={index}
                                        trigger={trigger}
                                        onTriggerClick={onPostKebabClick}
                                        post={postModel}
                                        user={currentUser}
                                    />
                                )}
                            />
                        </Box>
                    );
                }

                if (postCollection.isLoading) {
                    return (
                        <Box>
                            <PostCardCompactSkeleton />
                        </Box>
                    );
                }

                return null;
            }}
        />
    );
};
